/** This file handles table data & interactions */
import mori from 'mori';
import { buildTable } from './dataFromOptions';
import { default as tableConfig, LocationFilter } from '../config/table';
import {
  getEstTextLength,
  getEstTextRows,
} from '../util/util';
import {
  calcScrollShadow,
} from './table/util';

// Store for table
let defaultHeight = '';

export default function(
  sheetData,
  options,
  filteredData, // Data organized by timeframe
  combinedFilters,
  readyCb,
  selectedKeys,
  isMultiGeo = false,
  reduxCallback,
  generalCallback = false
) {
  // Hash for seeing changing table data
  this.tableHash = this.tableHash || false;
  this.tableInit = this.tableInit || false;
  // If no table (table init),
  // or isDirty, lets reload the data... time to PIVOT (but not a real pivot)!
  const isDirty = isTableDirty.bind(this)(filteredData, combinedFilters);
  if (this.tableInit === false || isDirty) {
    // Load the config
    const { rowData, columnDefs } = getData(
      sheetData,
      options,
      filteredData,
      combinedFilters
    );

    // Set filter for columnDefs
    // If not multigeo, setup location filter.
    // Multigeo filtering is handled by external filtering function
    if (!isMultiGeo) {
      columnDefs[0] = Object.assign({}, {
        filter: LocationFilter,
        filterParams: {
          apply: false,
          newRowsAction: 'keep',
        },
      }, columnDefs[0]);
    }

    // Check if table has not been initialized using a known prop
    if (!this.tableInit) {
      this.tableInit = true;
      // Table not initialized, so lets resetup
      Object.assign(this, {}, tableConfig, {
        rowData,
        columnDefs,
        'datacenterColumnKeys': mori.toJs(mori.map(
          (item) => mori.first(item),
          mori.filter((item) => item, combinedFilters[4]),
        )),
        'isExternalFilterPresent': () => isMultiGeo,
        'forPrint': options.print === 'true',
        'trend': options.activeTab === 'graph',
      });

      tableSetup.bind(this)(readyCb);
    } else {
      // Is Dirty
      // If the view has changed we need to reinit column defs as well
      // Table is already initied;
      this.api.setRowData(rowData);
      // @TODO, limit this to only triggger on view
      // changes between graph/(table || map)
      this.api.setColumnDefs(columnDefs);
      // Refreshing
      this.api.refreshHeader();
      this.api.refreshView();
      // Re-set whether this is a trend graph view
      this.trend = options.activeTab === 'graph';
    }
  }

  this.doesExternalFilterPass = doesExternalFilterPass.bind(this)(selectedKeys);
  if (reduxCallback) {
    this.reduxCallback = reduxCallback;
  }
  if (generalCallback) {
    generalCallback(this);
  }
  return this;
}

/**
 * Filter data
 *
 * Can either be imported into a file, OR they can be used via `table.X`
 */
export function filterData(data = null, callback) {
  if (!this.api) {
    return;
  } else if (data) {
    this.datacenterColumnKeys.forEach((columnKey) => {
      const filter = this.api.getFilterApi(columnKey);

      const filtered = mori.intoArray(mori.flatten(mori.reduce(
        (acc, item) => mori.conj(
          acc,
          mori.last(mori.last(mori.first(mori.filter(
            (test) => mori.first(mori.first(mori.first(test))) === columnKey,
            item
          ))))
        ),
        mori.vector(),
        data
      )));
      // Check that there is actually filtered response
      if (filtered.length > 0 && filtered[0] !== null) {
        filter.setModel(filtered);
      } else {
        filter.setModel([]);
      }
    });
  }
  this.api.onFilterChanged();
  if (callback) {
    callback();
  }
}

/**
 * Column hiding/showing
 * Can either be imported into a file, OR they can be used via `table.X`
 */
export function filterDistributions(
  distributions,
  options,
  timeframes,
  callback
) {
  if (!this.columnApi) {
    return;
  }

  let test = mori.intoArray(mori.map(
    (item) => mori.count(item) === 3 ?
      `${mori.first(item)}__${mori.first(mori.rest(item))}` :
      mori.first(item),
    distributions
  ));

  if (options.activeTab === 'graph') {
    test = test.concat(
      timeframes.reduce(
        (acc, curr) => acc.concat(...test
          .filter((item) => item !== 'Location')
          .map((item) => `${curr}__${item}`)),
        []));
  }

  const columns = [].concat.apply([], (this.columnApi.getAllColumns() || [])
    .map((item) => item.children ? item.children.map(
      (child) => child.colId) : item.colId));

  const hidden = columns.filter((def) => test.indexOf(def) === -1);

  const visible = columns.filter((def) => test.indexOf(def) !== -1);

  if (hidden.indexOf(
      (this.api.getSortModel()[0] || { colId: [] }
    ).colId) > -1) {
    this.api.setSortModel([{
      colId: 'Location',
      sort: 'asc',
    }]);
  }

  this.columnApi.setColumnsVisible(hidden, false);
  this.columnApi.setColumnsVisible(visible, true);

  if (callback) {
    callback();
  }
}

/**
 * Based on what the user has selected, see if the table rows
 * contain each column key for the multi-geo table
 *
 * This filter is used only for multi-geo tables.
 * @param  {object}  selectedKeys    the column keys that were selected by
 *  the user
 * @return {Function}
 */
function doesExternalFilterPass(selectedKeys) {
  // Build the filter keys based on what's been selected
  // We default to an '@' as some cell values are null, which causes filtering
  // to break
  const filteredKeys = this.datacenterColumnKeys.reduce((acc, columnKey) => {
    acc.push({
      key: columnKey,
      selected: mori.intoArray(mori.flatten(mori.reduce(
        (acc1, item) => mori.conj(
          acc1,
          mori.last(mori.last(mori.first(mori.filter(
            (test) => mori.first(mori.first(mori.first(test))) === columnKey,
            item
          )) || '@'))
        ),
        mori.vector(),
        selectedKeys
      ))),
    });
    return acc;
  }, []);

  // If nothing's selected, we have no need to filter.
  const noFiltering = mori.count(selectedKeys) === 0 ? true : false;

  // The filtering function.
  return (node) => {
    if (noFiltering) {
      return true;
    }
    let passes = false;
    const lastFilterKey = filteredKeys[filteredKeys.length - 1];
    // For each selected item, see if the following is true:
    // 1.  The data in the table equals what has been selected
    // 2.  Or, the selected from the last filter is the '@' character -
    //     This indicates we want to match on only the first column.
    filteredKeys[0].selected.forEach((selectedItem, index) => {
      if (
        node.data[filteredKeys[0].key] === selectedItem.split(' (All)')[0] &&
        (node.data[lastFilterKey.key] === lastFilterKey.selected[index] ||
          lastFilterKey.selected[index] === '@')
      ) {
        passes = true;
      }
    });
    return passes;
  };
}

/**
 * Table Setup
 * Access the `table` file global directly (it's scoped to this file)
 */
function tableSetup(readyCb) {
  // The initial setup for tableheight is 75% of the window's height
  defaultHeight = window.innerHeight * 0.75;
  // Set Iniital Height
  this.height = defaultHeight;

  // Assign functions for modification
  this.filterDistributions = filterDistributions.bind(this);
  this.filterData = filterData.bind(this);

  this.onGridReady = readyCb;
  this.onAfterFilterChanged = onAfterFilterChanged.bind(this);
  this.onAfterSortChanged = onAfterSortChanged.bind(this);
  this.getRowHeight = getRowHeight;
}

export function onAfterFilterChanged() {
  // We need to reset the width of the body
  // immediately after filter change so the table renders correctly
  if (!this.forPrint) {
    this.api.gridPanel.eBody.style.width = '';
  }

  const gridPanel = this.api.gridPanel;

  // Given the displayed columns, generate a unique list of header names
  const displayedHeaderNames =
    (this.columnApi.getAllDisplayedColumns() || [])
      .reduce((acc, column) => {
        if (acc.indexOf(column.colId.split('__')[0]) === -1) {
          acc.push(column.colId.split('__')[0]);
        }
        return acc;
      }, []);

  this.displayedHeaders = (this.columnApi.getAllDisplayedColumns() || [])
    .reduce((acc, column) => {
      if (column.colId.indexOf('__') === -1) {
        acc.push(column.colDef);
        return acc;
      }

      const headerGroup = column.colId.split('__')[0];
      const headerIndex = displayedHeaderNames.indexOf(headerGroup);

      acc[headerIndex] =
        (acc[headerIndex] || {
          headerName: headerGroup,
          children: [],
        });
      acc[headerIndex].children.push(column.colDef);
      return acc;
    }, []);

  // Using the above header names, build a list of headers (and children)
  // This will allow us to calculate header height on only the visible headers
  const headerHeight = resizeHeader.bind(this)({
    columns: this.displayedHeaders,
    forPrint: this.forPrint,
  });

  if (!this.forPrint) {
    // Ensure top row is visible post header height adjustment
    this.api.ensureIndexVisible(0);

    // 20 is added to adjust for possible x-overflow
    const gridHeight =
      Math.round(
        gridPanel.eBodyContainer.getBoundingClientRect().height +
          headerHeight + 20
      );

    this.height = (gridHeight > defaultHeight) ? defaultHeight : gridHeight;

    // @TODO: Hacky.  If it's a short dataset, the y-scroll will appear
    // likely due to a height calc issue due to borders :-/
    gridPanel.eBodyViewport.style.overflowY = gridHeight > defaultHeight ?
      'auto' : 'hidden';

    setBodySize(this);
    calcScrollShadow(this.height);
  }

  this.api.refreshRows(
    this.api.getRenderedNodes()
  );
}

export function onAfterSortChanged() {
  if (!this.forPrint) {
    this.api.ensureIndexVisible(0);
  }
  if (this.reduxCallback) {
    const sortModel = this.api.getSortModel()[0];
    this.reduxCallback({ sortModel: sortModel });
  }
  this.api.refreshRows(
    this.api.getRenderedNodes()
  );
}

export function getRowHeight(params) {
  const footnoteFontString =
    'normal 5px/0 Arial, sans-serif';
  const fontStyleString =
    'bold 13px/20px "Droid Sans", helvetica, sans-serif';
  const rowDefaultHeight = 20;
  let height = rowDefaultHeight;
  const displayedColumns = params
    .api
    .columnController
    .allDisplayedColumns
    .reduce(
      (acc, curr) => Object.assign(acc, { [curr.colId]: curr.colDef.width }),
      {}
    );
  Object.keys(params.node.data)
    .filter((node) => displayedColumns.hasOwnProperty(node))
    .forEach((key) => {
      // Get normal width...
      let width = getEstTextLength(params.node.data[key], fontStyleString);
      let string = params.node.data[key];
      // In english:
      //  footnoteCallback() & sortMobel() are both existing
      //  current key is a geo (location/state)
      //  sorted row is not geo (so geo is then ranked)
      if (params.api.footnoteCallback &&
        params.api.getSortModel &&
        (key === 'Location' || key === 'State') &&
        (params.api.getSortModel()[0] || { colId: null }).colId !== key
      ) {
        string = `1. ${string}`;
        width = getEstTextLength(string, fontStyleString);
      }

      // Get footnotes
      if (params.api.footnoteCallback) {
        const footnotes = params.api.footnoteCallback([
          params.node.data[key],
          key,
        ])
          .map(({ label }) => ` ${label}`)
          .join(' ');
        // 6 is a a magic number for the `/s` char
        width = width + getEstTextLength(footnotes, footnoteFontString) + 6;
        string = `${string} ${footnotes}`;
      }

      // For the edge case where there is a fraction of a pixel difference
      // we want to see if the width difference is less than 1, not 0.
      if ((displayedColumns[key] - width) < 1) {
        // Lets actually calculate the height in this case so we know it will work fine.
        const heightMultiplier = getEstTextRows(
          string,
          fontStyleString,
          displayedColumns[key]
        );
        height = Math.max(rowDefaultHeight * heightMultiplier, height);
      }
    });
  return height + 10; // add padding
}

/**
 * Set body size contain when needed based on visible columns
 *
 * @param  {array}  displayedColumns    Array of columns currently displayed in the table
 */
function setBodySize(table) {
  // Container width needs to be set.
  // Body width should only be set if it is more narrow
  // than the overall table wrapper.
  const displayedColumns = table.columnApi.getAllDisplayedColumns();
  const containerWidth =
    mori.intoArray(mori.rest(displayedColumns))
      .reduce((acc, curr) => acc + curr.colDef.width, 0);
  const bodyWidth = displayedColumns[0].colDef.width +
    containerWidth;

  table.api.gridPanel.eBodyContainer.style.width =
    `${containerWidth}px`;

  // We need to reset the width in order for headers and table
  // to display correctly.
  if (bodyWidth < document.querySelector('.table-wrapper').clientWidth) {
    table.api.gridPanel.eBody.style.width =
      `${bodyWidth + 18}px`;
  }
}

/**
 * Resize Header
 */
function resizeHeader(params) {
  const parentElm = this.api.gridCore.eGridDiv.offsetParent;

  // If there are no 'with-group' headers, but we have 'no-group' headers,
  // we need to adjust the parentHeight below
  const adjustParentHeight =
    !!!parentElm.querySelector('.ag-header-group-cell-with-group') &&
      !!parentElm.querySelector('.ag-header-group-cell-no-group');
  // Used to adjust parentHeight.  If our header container has more than
  // 1 row, we're grouping headers.
  const hasGroupedHeader =
    parentElm.querySelector('.ag-header-container')
      .querySelectorAll('.ag-header-row').length > 1;
  const fontStyleString = (this.headerStyle) ?
    this.headerStyle :
    'bold 14px/20px "Droid Sans", helvetica, sans-serif';
  const baseHeaderHeight = 20;
  const heightAdjust = 10; // Adjustment for after height is calculated
  const padding = 30;

  let { header: headerHeight, parent: parentHeight } = (params.columns || [])
    .reduce(({ header, parent }, column) => {
      if (column.children) {
        // After header height calculation for parent/grouped row
        const heightMultiplier = getEstTextRows(
          column.headerName,
          fontStyleString,
          column.children.reduce((acc, { width }) => acc + width, 0) - 16
        );
        const groupHeight = Math.max(
          parent,
          baseHeaderHeight * heightMultiplier
        );
        return {
          // For each child, find the largest height for the header
          header: column.children.reduce((acc, child) => {
            const childHeightMultiplier = getEstTextRows(
              child.headerName,
              fontStyleString,
              child.width - padding
            );

            return Math.max(
              acc,
              baseHeaderHeight * childHeightMultiplier
            );
          }, header),
          parent: groupHeight,
        };
      }
      // Else
      const heightMultiplier = getEstTextRows(
        column.headerName,
        fontStyleString,
        column.width - padding
      );
      return {
        header: Math.max(
          header,
          baseHeaderHeight * heightMultiplier
        ),
        parent,
      };
    }, { header: baseHeaderHeight, parent: 0 });

  headerHeight += heightAdjust;

  parentHeight = parentHeight !== 0 && parentHeight < baseHeaderHeight ?
    baseHeaderHeight + heightAdjust :
    parentHeight + (parentHeight !== 0 ? heightAdjust : 0);

  // Sets value for parent height.  Used in maps tab where headers can
  // get very tall if parentHeight is kept at 0.
  parentHeight = adjustParentHeight && hasGroupedHeader ?
    baseHeaderHeight : parentHeight;

  this.api.setHeaderHeight((parentHeight !== 0) ?
    (parentHeight + headerHeight) / 2 :
    headerHeight);

  // If there is a parent height, we need to adjust node heights
  // as AG-Grid does not handle this for us.
  if (parentHeight !== 0) {
    // Set parent height on first header row
    [].forEach.call(
      parentElm.querySelectorAll('.ag-header-row:first-child'),
      (row) => row.style.height = `${parentHeight}px`
    );
    // Set height and top on 2nd row below parent row
    [].forEach.call(
      parentElm.querySelectorAll('.ag-header-row:last-child'),
      (row) => {
        row.style.height = `${headerHeight}px`;
        row.style.top = `${parentHeight}px`;

        // If we are printing, we need to set height on
        // All cells in the last row when there is a parent row.
        if (params.forPrint) {
          [].forEach.call(
            row.querySelectorAll('.ag-header-cell'),
            (cell) => cell.style.height = `${headerHeight}px`
          );
        }
      }
    );

    if (params.forPrint) {
      [].forEach.call(
        parentElm.querySelectorAll('.ag-header-group-cell-with-group'),
        (groupHeader) => groupHeader.style.height = `${parentHeight}px`
      );
    }
  }

  return parentHeight + headerHeight;
}

/**
 * Get and set the table data
 */
function getData(sheetData, options, filteredData, combinedFilters) {
  const data = mori.toJs(buildTable(
    sheetData,
    options,
    filteredData,
    ...combinedFilters
  ));

  return {
    'rowData': data.tableData.rowData,
    'columnDefs': data.tableData.columnDefs,
  };
}

/**
 * See if the table actually needs to be rebuilt, uses the tablehash var
 * 1. Change of timeframe
 * 2. Change of datatype
 * 3. Change of Tab (Timeframe's changing signal this due to length)
 *   a. Table/Map -> Graph
 *   b. Graph -> Table/Map
 * @param  {string}  view    the current tab view ('graph', 'map', 'table')
 * @param  {object}  filters the combined filters
 * @return {Boolean}
 */
function isTableDirty(timeframes, filters) {
  const tempHash = mori.hash(mori.into(
    mori.vector(filters[0]),
    mori.into(
      mori.vector(filters[3]),
      mori.reduce((acc, item) => mori.conj(acc, mori.first(item)),
        mori.vector(),
        timeframes
      ))));

  if (this.tableHash && this.tableHash === tempHash) {
    return false;
  }
  this.tableHash = tempHash;
  return true;
}



// WEBPACK FOOTER //
// ../datacenter/app/js/services/tableData.js